import { useState } from 'react';
import { API_BASE } from '../config';
import { formatDate } from '../utils/formatDate';

// PO & SO Lifecycle — Packed Box Images. PO-number-only search: resolves
// through so_headers/invoice_headers under the hood (so an image filed
// under the SO voucher number instead of the PO number is still found),
// then fuzzy-matches (digit-core fast path + >= 95% similarity fallback)
// directly against the po-box-images blob container. Not to be confused
// with pages/PoBoxImages.jsx, the mobile capture/upload page used from
// WarehouseHome.jsx — this is the search/lookup counterpart.

// Downloads a list of {url, filename} sequentially. Tries fetch->blob first
// (forces a real "Save As" regardless of the resource being cross-origin);
// if the storage account's CORS rules don't allow fetch(), falls back to
// opening the image in a new tab so the user can still save it manually.
async function downloadOne(url, filename) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error('fetch failed');
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

async function downloadMany(items) {
  for (let i = 0; i < items.length; i++) {
    await downloadOne(items[i].url, items[i].filename);
    if (i < items.length - 1) await new Promise(r => setTimeout(r, 250));
  }
}

export default function PackedBoxImages() {
  const [query, setQuery] = useState('');
  const [soMatches, setSoMatches] = useState([]);
  const [invoiceMatches, setInvoiceMatches] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [downloading, setDownloading] = useState(false);

  async function runSearch(e) {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError('');
    setSelectMode(false);
    setSelected(new Set());
    try {
      const res = await fetch(`${API_BASE}/po-box-images-search?q=${encodeURIComponent(query.trim())}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Search failed');
      setSoMatches(data.so_matches || []);
      setInvoiceMatches(data.invoice_matches || []);
      setImages(data.images || []);
      setSearched(true);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }

  function toggleSelected(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toImageItem(img, idx) {
    const ext = (img.blob_name || '').match(/\.[a-zA-Z0-9]+$/)?.[0] || '.jpg';
    return { url: img.view_url, filename: `${img.po_number || 'box'}_${idx + 1}${ext}` };
  }

  async function handleDownloadSelected() {
    const items = images
      .map((img, idx) => ({ img, idx }))
      .filter(({ img }) => selected.has(img.id))
      .map(({ img, idx }) => toImageItem(img, idx))
      .filter(item => item.url);
    if (items.length === 0) return;
    setDownloading(true);
    await downloadMany(items);
    setDownloading(false);
  }

  async function handleDownloadAll() {
    const items = images.map(toImageItem).filter(item => item.url);
    if (items.length === 0) return;
    setDownloading(true);
    await downloadMany(items);
    setDownloading(false);
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <h1 style={S.h1}>Packed Box Images</h1>
        <div style={{ fontSize: 13, color: '#8C82A0', marginTop: 4 }}>
          Search by PO number to find dispatch box photos, even if the image was filed under a different identifier (e.g. the SO voucher number).
        </div>
      </div>

      <form onSubmit={runSearch} style={S.searchForm} data-tutorial="box-images-search">
        <div style={S.searchRow}>
          <input
            type="text"
            autoFocus
            placeholder="Enter PO number"
            value={query}
            onChange={e => setQuery(e.target.value)}
            style={S.searchInput}
          />
          <button type="submit" disabled={loading} style={S.btnPrimary}>
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>
      </form>

      {error && <div style={{ ...S.banner, background: '#FBF1F3', color: '#C25E77' }}>{error}</div>}

      {searched && !loading && (
        <>
          {(soMatches.length > 0 || invoiceMatches.length > 0) && (
            <div style={S.matchInfo}>
              {soMatches.map(so => (
                <span key={`so-${so.voucher_no}`} style={S.matchPill}>
                  SO {so.voucher_no} · PO {so.order_ref_no || '—'} · {so.buyer || so.consignee || '—'} · {formatDate(so.so_date)}
                </span>
              ))}
              {invoiceMatches.map(inv => (
                <span key={`inv-${inv.invoice_no}`} style={S.matchPill}>
                  Invoice {inv.invoice_no} · PO {inv.order_ref_no || '—'} · {inv.buyer || inv.consignee || '—'} · {formatDate(inv.invoice_date)}
                </span>
              ))}
            </div>
          )}

          <div style={S.toolbar}>
            <div style={S.sectionTitle}>Images ({images.length})</div>
            {images.length > 0 && (
              <div style={{ display: 'flex', gap: 8 }}>
                {!selectMode ? (
                  <button onClick={() => setSelectMode(true)} style={S.btnGhost}>Download</button>
                ) : (
                  <>
                    <button onClick={handleDownloadAll} disabled={downloading} style={S.btnGhost}>
                      {downloading ? 'Downloading…' : 'Download All'}
                    </button>
                    <button onClick={handleDownloadSelected} disabled={downloading || selected.size === 0} style={S.btnPrimary}>
                      {downloading ? 'Downloading…' : `Download Selected (${selected.size})`}
                    </button>
                    <button onClick={() => { setSelectMode(false); setSelected(new Set()); }} style={S.btnGhost}>Cancel</button>
                  </>
                )}
              </div>
            )}
          </div>

          {images.length === 0 ? (
            <div style={S.emptyBox}>No box images found for "{query}".</div>
          ) : (
            <div style={S.grid}>
              {images.map(img => {
                const isSelected = selected.has(img.id);
                const Tile = selectMode ? 'div' : 'a';
                const tileProps = selectMode
                  ? { onClick: () => toggleSelected(img.id) }
                  : { href: img.view_url || '#', target: '_blank', rel: 'noopener noreferrer' };
                return (
                  <Tile key={img.id} style={{ ...S.tile, cursor: selectMode ? 'pointer' : 'pointer', position: 'relative' }} {...tileProps}>
                    {selectMode && (
                      <div
                        style={{ ...S.selectCircle, ...(isSelected ? S.selectCircleActive : {}) }}
                        onClick={e => { e.stopPropagation(); toggleSelected(img.id); }}
                      >
                        {isSelected && '✓'}
                      </div>
                    )}
                    {img.view_url ? (
                      <img src={img.view_url} alt={img.po_number} style={S.thumb} />
                    ) : (
                      <div style={{ ...S.thumb, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9A90AE', fontSize: 12 }}>
                        No preview
                      </div>
                    )}
                    <div style={S.tileMeta}>
                      <div style={{ fontWeight: 600 }}>{img.po_number}</div>
                      <div>{img.uploaded_by || '—'} · {formatDate(img.uploaded_at)}</div>
                      <div style={{ color: '#8C82A0' }}>{Math.round(img.matched_score * 100)}% match</div>
                    </div>
                  </Tile>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const S = {
  page: { fontFamily: "'Poppins', sans-serif", color: '#231C30' },
  header: { marginBottom: 18 },
  h1: { fontSize: 24, fontWeight: 700, margin: 0 },
  banner: { padding: '10px 16px', borderRadius: 10, fontSize: 13, marginBottom: 16 },
  searchForm: { marginBottom: 20, maxWidth: 560 },
  searchRow: { display: 'flex', gap: 10 },
  searchInput: { flex: 1, padding: '10px 14px', borderRadius: 9, border: '1px solid #E4DEEE', fontSize: 13.5, fontFamily: "'Poppins'" },
  btnPrimary: { padding: '10px 20px', borderRadius: 9, border: 'none', background: '#4A6FA5', color: '#fff', fontWeight: 600, fontSize: 13.5, cursor: 'pointer', fontFamily: "'Poppins'" },
  btnGhost: { padding: '9px 16px', borderRadius: 9, border: '1px solid #E4DEEE', background: '#fff', color: '#231C30', fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: "'Poppins'" },
  matchInfo: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 },
  matchPill: { padding: '6px 12px', borderRadius: 20, background: '#E5EEF7', color: '#2F4C6E', fontSize: 12, fontWeight: 600 },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  sectionTitle: { fontSize: 14, fontWeight: 700 },
  emptyBox: { padding: 30, textAlign: 'center', color: '#9A90AE', fontSize: 13, background: '#fff', border: '1px solid #ECE7F2', borderRadius: 14 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 },
  tile: { display: 'block', background: '#fff', border: '1px solid #ECE7F2', borderRadius: 12, overflow: 'hidden', textDecoration: 'none', color: '#231C30' },
  thumb: { width: '100%', height: 140, objectFit: 'cover', background: '#F4F1F8' },
  tileMeta: { padding: '8px 10px', fontSize: 11.5, lineHeight: 1.5 },
  selectCircle: {
    position: 'absolute', top: 8, right: 8, width: 22, height: 22, borderRadius: '50%',
    background: 'rgba(255,255,255,0.9)', border: '2px solid #4A6FA5', display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
    color: '#fff', cursor: 'pointer', zIndex: 2,
  },
  selectCircleActive: { background: '#4A6FA5' },
};
